/**
 * Display formatting. All timestamp formatting goes through here so the THREE freshness labels are
 * rendered consistently and a tz-aware ISO instant is ALWAYS rendered in the portal's single operating
 * timezone (Asia/Bangkok) — never the host machine's local clock (the operator travels) and never a
 * bare ambiguous HH:MM. This is the design's hardest lesson; see sync-now service _live_at.
 */

import { parseISO, isValid } from "date-fns";

/**
 * The portal runs against one timezone (Bangkok / ICT). We pin display to it explicitly via Intl so a
 * freshness time reads the same regardless of the host clock — an operator on a UTC/PST laptop must NOT
 * silently see a wrong wall-clock for the one signal the design treats as sacred.
 */
const PORTAL_TZ = "Asia/Bangkok";

const INSTANT_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: PORTAL_TZ,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: PORTAL_TZ,
  month: "short",
  day: "numeric",
  year: "numeric",
});

/**
 * Date+time for a freshness label in Bangkok wall-clock, e.g. "24 Jun 2026 09:00". Pinned to the
 * portal timezone (the operator travels) so a +07 instant is never mistaken for the host's local
 * clock. Null/invalid -> em dash.
 */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  if (!isValid(d)) return "—";
  const parts = INSTANT_FMT.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")}`;
}

/** A calendar day with no time: printed as that day, in no timezone. */
const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
});

/**
 * Date only, e.g. "Jun 24, 2026". An instant prints its Bangkok calendar date. A date-only
 * "YYYY-MM-DD" is ALREADY a calendar day (the API sends Zoom's dates as the Bangkok day, wave 8) and
 * prints as that day on every host: parsing it as host-local midnight and re-reading it in Bangkok
 * would move it a day back on a laptop east of UTC+7 (Singapore, Tokyo).
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const day = DAY_ONLY.exec(iso);
  if (day) {
    const [y, m, dd] = [Number(day[1]), Number(day[2]), Number(day[3])];
    const d = new Date(Date.UTC(y, m - 1, dd));
    // Date.UTC rolls "2026-13-45" over into 2027; reject it as parseISO did.
    return d.getUTCMonth() === m - 1 && d.getUTCDate() === dd ? DAY_FMT.format(d) : "—";
  }
  const d = parseISO(iso);
  if (!isValid(d)) return "—";
  return DATE_FMT.format(d);
}

/** Integer with thousands separators (tabular). */
export function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

/** Percent, one decimal where useful, e.g. "76%" / "36.8%". */
export function formatPct(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const rounded = Math.round(n * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

/**
 * Money. Returns null when the amount is absent — callers MUST treat null as "render nothing"
 * (a non-money role has the field stripped entirely, never zero). Currency optional; defaults to no
 * symbol so a missing currency never fabricates one.
 *
 * Cent-exact by DEFAULT (real Zoom invoice amounts reconcile to the cent — see the invoice-extract
 * work — so we never silently round a financial figure). Pass precision=0 for aggregate ARR tiles
 * where whole-currency reads cleaner and exactness isn't being reconciled.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  precision: 0 | 2 = 2,
): string | null {
  // Postgres NUMERIC serializes to a JSON string ("12.74"), so money may arrive as a string — coerce
  // it once here. An empty string means "no amount" (render nothing), like null.
  const n = amount === "" ? null : typeof amount === "string" ? Number(amount) : amount;
  if (n === null || n === undefined || Number.isNaN(n)) return null;
  try {
    if (currency) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(n);
    }
  } catch {
    // Unknown currency code → fall through to a plain number.
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(n);
}
